// Tasks > Stats and Alerts client (docs/TASK-STATS-SPEC.md). Reads go through
// the resource store; the one write (Reviewed) uses authedWrite.
import { authedGet, authedWrite } from './apiClient';

export interface StatsTask { key: string; label: string; kind: 'report' | 'completion' }
export interface StatsTaskList { tasks: StatsTask[]; locations: string[] }
export interface ReportPoint {
  report_date?: string; week_start?: string; drive_file_id: string | null; file_name?: string | null;
  status?: string; values: Record<string, number>;
}
export interface CompletionPoint {
  period_label: string; period_start: string; period_end: string; total: number; done: number;
  pct_done: number | null; pct_on_time: number | null; sent_back: number; days_late: number;
}
export interface TaskStatsData {
  task: string; kind: 'report' | 'completion'; locations: string[];
  series: Record<string, (ReportPoint | CompletionPoint)[]>;
}
export type AlertType = 'WAIVED_PAYMENT' | 'POINTS' | 'REFUND' | 'DISCOUNT';
export const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  WAIVED_PAYMENT: 'Waived payment', POINTS: 'Points', REFUND: 'Refund', DISCOUNT: 'Discount',
};
export interface TaskAlert {
  id: string | number; location_code: string; type: AlertType; alert_date: string;
  client_name: string | null; detail: string | null; amount: number | null; points: number | null;
  employee: string | null; report_date: string; drive_file_id: string | null; file_name: string | null;
  reviewed: { by: string; at: string } | null;
}
export interface TaskAlertsData { alerts: TaskAlert[]; locations: string[]; truncated?: boolean }
export interface AlertsSummary { unreviewed: number; by_clinic: Record<string, number> }

export const TASK_STATS_LIST_KEY = 'task-stats-list';
export const ALERTS_SUMMARY_KEY = 'task-alerts-summary';
export const statsKey = (task: string): string => `task-stats:${task}`;
export const alertsKey = (type: string, from: string, to: string): string => `task-alerts:${type}|${from}|${to}`;

export const fetchStatsList = (): Promise<StatsTaskList> => authedGet('/api/v1/task-stats/tasks');
export const fetchStats = (task: string): Promise<TaskStatsData> =>
  authedGet(`/api/v1/task-stats?task=${encodeURIComponent(task)}`);
export function fetchAlerts(type: string, from: string, to: string): Promise<TaskAlertsData> {
  const q = new URLSearchParams();
  if (type) q.set('type', type);
  if (from) q.set('from', from);
  if (to) q.set('to', to);
  return authedGet(`/api/v1/task-alerts?${q.toString()}`);
}
export const fetchAlertsSummary = (): Promise<AlertsSummary> => authedGet('/api/v1/task-alerts/summary');
export const reviewAlert = (id: string | number): Promise<unknown> =>
  authedWrite('POST', `/api/v1/task-alerts/${id}/review`, {});

export const driveFileUrl = (id: string): string => `https://drive.google.com/file/d/${encodeURIComponent(id)}/view`;

/** Default alert window: the last 30 days (matches the server default). */
export function defaultFrom(today: Date = new Date()): string {
  const d = new Date(today.getTime() - 30 * 86_400_000);
  return d.toISOString().slice(0, 10);
}
